/**
 * Diagram Data Source Connections Hook
 * TanStack Query hook for managing diagram-to-source connections
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type { DiagramDataSourceConnection, FieldMapping } from '../models/DiagramDataSourceConnection';
import type { DataSource } from '../models/DataSource';

// Query key factory
export const connectionKeys = {
  all: ['diagram-connections'] as const,
  forDiagram: (diagramId: string) =>
    [...connectionKeys.all, diagramId] as const,
};

interface ConnectionWithSource extends DiagramDataSourceConnection {
  dataSource: DataSource | null;
}

interface ConnectionsResponse {
  connections: ConnectionWithSource[];
}

interface ConnectionResponse {
  connection: DiagramDataSourceConnection;
}

/**
 * Hook to fetch all data source connections for a diagram
 */
export function useDiagramConnections(diagramId: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery<ConnectionsResponse>({
    queryKey: connectionKeys.forDiagram(diagramId ?? ''),
    queryFn: async () => {
      const res = await authenticatedFetch(
        `/api/diagrams/${diagramId}/data-source-connections`
      );
      if (!res.ok) throw new Error('Failed to load connections');
      return res.json();
    },
    enabled: !!diagramId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for diagram connection mutations
 */
export function useDiagramConnectionMutations(diagramId: string) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  const create = useMutation({
    mutationFn: async (input: {
      dataSourceId: string;
      fieldMappings?: FieldMapping[];
    }) => {
      const res = await authenticatedFetch(
        `/api/diagrams/${diagramId}/data-source-connections`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        }
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create connection');
      }
      return res.json() as Promise<ConnectionResponse>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: connectionKeys.forDiagram(diagramId),
      });
    },
  });

  const updateMappings = useMutation({
    mutationFn: async ({
      connectionId,
      fieldMappings,
    }: {
      connectionId: string;
      fieldMappings: FieldMapping[];
    }) => {
      const res = await authenticatedFetch(
        `/api/diagrams/${diagramId}/data-source-connections/${connectionId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fieldMappings }),
        }
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update mappings');
      }
      return res.json() as Promise<ConnectionResponse>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: connectionKeys.forDiagram(diagramId),
      });
    },
  });

  const remove = useMutation({
    mutationFn: async (connectionId: string) => {
      const res = await authenticatedFetch(
        `/api/diagrams/${diagramId}/data-source-connections/${connectionId}`,
        {
          method: 'DELETE',
        }
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete connection');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: connectionKeys.forDiagram(diagramId),
      });
    },
  });

  return { create, updateMappings, remove };
}
